import { t } from "i18next";
import { Card, IconButton, Typography } from "@material-tailwind/react";
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import EditCreateDrawer from "../../../components/edit-create-drawer/EditCreateDrawer";
import CustomerForm from "../../../components/data/customer/CustomerForm";
import {
  AutomaticReportSendLabel,
  resolveAutomaticReportSendMode
} from "../../../components/data/customer/AutomaticReportSendSelect";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../../services/api/error";
import { useEffect, useState } from "react";

import { api } from "../../../services/api/api";
import { isOdooConnected, hasExternalId, saveExternalId } from "../../../services/odoo/odoo";
import { resolveActivitySectorLabel } from "../../../components/data/activity-sector/activitySectorLabel";
import {
  isCustomerLanguageLink,
  resolveCustomerLanguagesForDisplay
} from "../../../components/data/customer/customerLanguages";
import CustomerStatusBadge from "../../../components/data/customer-status/CustomerStatusBadge";
import { CustomerType } from "../../../types/customer/customerType";
import CustomerReceivables from "../../../components/data/customer/CustomerReceivables";
import { useDrawerLayoutDispatch } from "../../../components/drawer-layout/DrawerLayout";
import { useUserRoles } from "../../../hooks/useUserRoles";
import AdminCustomerPatchDrawer from "../../super-admin/views/customers/AdminCustomerPatchDrawer";

/**
 * Informations Tab Component
 */
const InformationsTab = ({ 
  data,
  onChange
}: { 
  data: EasyTech.Customer,
  onChange: (data: EasyTech.Customer) => void
}) => {

  const [displayData, setDisplayData] = useState<EasyTech.Customer>(data);
  const dispatch = useDrawerLayoutDispatch();
  const { isSuperAdmin } = useUserRoles();

  /**
   * Correction partielle : la porte qui reste ouverte sur une fiche incomplète.
   *
   * Le bouton « Modifier » voisin passe par le formulaire client complet, qui valide avant
   * d'enregistrer — NIP, secteur, adresse, langues, email et téléphone. Une fiche legacy à qui il
   * manque l'un d'eux ne peut donc pas y recevoir de correction, pas même son identifiant Odoo,
   * qui vit pourtant dans ce formulaire. Le tiroir de correction n'exige que le nom.
   *
   * Réservé au super admin : il écrit sur /admin/**, et son identifiant Odoo emprunte un verbe
   * que seul ce rôle ouvre. Masquer le bouton est une politesse — l'API répond 403 de toute façon.
   */
  const openPatchDrawer = () => {
    dispatch({
      type: "open",
      title: t("admin:customers.patch.title"),
      content: <AdminCustomerPatchDrawer customerId={data.id} onPatched={refreshAfterPatch} />
    });
  };

  /**
   * Le tiroir ne peut pas rendre la fiche à jour : une correction qui ne déplace que l'identifiant
   * Odoo n'émet aucun PATCH, donc n'a aucune réponse à transmettre, et a pourtant changé la fiche.
   * On relit donc, et on laisse l'affichage intact si la relecture échoue — la correction, elle,
   * est déjà enregistrée.
   */
  const refreshAfterPatch = async (): Promise<void> => {
    try {
      const refreshed = await api.get("users/customers/" + data.id) as EasyTech.Customer;
      onChange(refreshed);
    } catch (err) {
      showApiErrorToast(err);
    }
  };

  useEffect(() => {
    if (data && data.languages && Array.isArray(data.languages) && data.languages.length > 0) {
      const firstLang = data.languages[0];
      
      if (isCustomerLanguageLink(firstLang)) {
        const links = data.languages.filter(isCustomerLanguageLink);
        
        api.get("users/languages").then((response: unknown) => {
          const allLanguages = (response as any) as EasyTech.Language[];
          const clientLanguages = resolveCustomerLanguagesForDisplay(allLanguages, links);
          
          setDisplayData(prev => ({
            ...prev,
            languages: clientLanguages
          }));
        }).catch(() => {
          setDisplayData(prev => ({
            ...prev,
            languages: resolveCustomerLanguagesForDisplay([], links)
          }));
        });
      } else {
        setDisplayData(data);
      }
    } else {
      setDisplayData(data);
    }
  }, [data]);

  /**
   * handleCustomerEdit - persist the drawer edits and refresh the view from the answer.
   *
   * PUT /users/customers/{id} answers with the DETAILED customer (CustomerCrudController in core),
   * so its payload carries reportTemplate, sendInterventionReportEmail, languages, contacts and
   * machines -- everything this view renders. It used to answer with a CustomerSimpleResponse,
   * which is why the detail card went stale until a page reload and why this used to re-read the
   * customer afterwards.
   *
   * The Odoo id goes first, through its own super-admin-only verb: the PUT below deliberately
   * cannot move it (CustomerService.merge pins the stored value), and failing first -- a 409 on an
   * id another customer already holds -- leaves the customer untouched. Once it lands, the PUT
   * re-reads it from the database, so the answer this view refreshes from already carries the new
   * value. saveExternalId calls nothing when the field did not change.
   *
   * A failure rejects, so the drawer stays open with the user's input.
   */
  const handleCustomerEdit = async (edited: EasyTech.Customer): Promise<void> => {
    try {
      await saveExternalId("customers", edited.id, data.externalId, edited.externalId);
    } catch (err) {
      console.error(err);
      showApiErrorToast(err, { fallbackKey: "client:odoo:externalId:updateError" });
      throw err;
    }

    try {
      const updated = await api.put("users/customers/" + edited.id, edited) as EasyTech.Customer;
      toast.success(t("common:customerEdited"));
      onChange(updated);
    } catch (err) {
      console.error(err);
      showApiErrorToast(err, { fallbackMessage: t("client:updateError", "Erreur lors de la mise à jour du client") });
      throw err;
    }
  };
 
  const getLanguageLabels = () => {
    if (!displayData.languages || displayData.languages.length === 0) {
      return "Aucune langue définie";
    }
    
    const firstLang = displayData.languages[0];
    
    if (firstLang && "id" in firstLang && "label" in firstLang) {
      return (displayData.languages as EasyTech.Language[]).map(lang => lang.label).join(", ");
    }
    
    return "Chargement des langues...";
  };

  const automaticReportSendMode = resolveAutomaticReportSendMode(data.sendInterventionReportEmail);

  return (
    <div>
      <div className="flex justify-between items-center">
        <Typography variant="h4" className="sub-title">
          {t("client:tabs:informations")}
        </Typography>

        <div className="flex gap-2">
          {isSuperAdmin && (
            <IconButton
              onClick={openPatchDrawer}
              variant="text"
              size="sm"
              title={t("admin:customers.patch.title")}
            >
              <WrenchScrewdriverIcon className="w-[20px] h-[20px]" />
            </IconButton>
          )}

          <EditCreateDrawer<EasyTech.Customer>
                  data={data}
                  FormComponent={CustomerForm}
                  isNotCreatable={true}
                  onSubmit={handleCustomerEdit}
                  entityName="customer"
                  editButtonText={t("common:edit")}
                />
        </div>
      </div>

      <Card className="details-card">
        <div className="field">
          <label>{t("common:status")}</label>
          {data.type ? (
            <div>
              <CustomerStatusBadge status={data.type as unknown as CustomerType} />
            </div>
          ) : (
            <Typography>-</Typography>
          )}
        </div>

        <div className="field">
          <label>{t("common:activitySector")}</label>
          {/* activitySector.label is a slug ("industry"); the display wording lives in the
              entity's translations. See resolveActivitySectorLabel for the resolution order. */}
          <Typography>{resolveActivitySectorLabel(data.activitySector) || "-"}</Typography>
        </div>

        {/*
          * The Odoo id is a realm-level concern: it only exists where Odoo is enabled. The gate is
          * deliberately isOdooConnected() alone, not the narrower
          * `isOdooConnected() && !isRomaniaRealm()` used by the Send-to-Odoo
          * button in Customer.tsx -- that exclusion is about offering a MANUAL sync, not about
          * whether an externalId exists. ro customers do carry one and it is worth reading.
          */}
        {isOdooConnected() && (
          <div className="field">
            <label>{t("client:odoo:externalId:label")}</label>
            <Typography>
              {hasExternalId(data.externalId)
                ? data.externalId
                : t("client:odoo:externalId:notSynced")}
            </Typography>
          </div>
        )}

        <div className="field">
          <label>{t("common:nip")}</label>
          <Typography>{data.nip}</Typography>
        </div>

        <div className="field">
          <label>{t("common:badPayer")}</label>
          {data.badPayer ? (
            <ExclamationCircleIcon color="red" className="w-[18px]" />
          ) : (
            <CheckCircleIcon color="green" className="w-[18px]" />
          )}
        </div>

        <div className="field">
          <label>{t("client:form:fields:purchaseOrderMandatory:label")}</label>
          {data.purchaseOrderMandatory ? (
            <ExclamationCircleIcon color="orange" className="w-[18px]" />
          ) : (
            <CheckCircleIcon color="green" className="w-[18px]" />
          )}
        </div>

        <div className="field">
          <label>{t("common:languages")}</label>
          <Typography>
            {getLanguageLabels()}
          </Typography>
        </div>

        {data.contactInfo && (
          <div className="field">
            <label>{t("common:email")}</label>
            <Typography>
              {data.contactInfo.emails && data.contactInfo.emails.length > 0
                ? data.contactInfo.emails.join(", ")
                : data.contactInfo.email || t("common:noEmail")}
            </Typography>
          </div>
        )}

        {data.contactInfo && (
          <>
            <div className="field">
              <label>{t("common:phone")}</label>
              <Typography>{data.contactInfo.phone || t("common:noPhone")}</Typography>
            </div>

            <div className="field">
              <label>{t("common:fax")}</label>
              <Typography>{data.fax || t("common:noFax")}</Typography>
            </div>
          </>
        )}

        <div className="field">
          <label>{t("client:form:fields:reportTemplate:label")}</label>
          <Typography>{data.reportTemplate?.name || t("settings:reports:templates:defaultSuffix", "default")}</Typography>
        </div>

        <div className="field">
          <label>{t("client:form:fields:automaticReportSend:label")}</label>
          <Typography>
            {automaticReportSendMode
              ? <AutomaticReportSendLabel mode={automaticReportSendMode} />
              : "-"}
          </Typography>
        </div>

      </Card>

      {/*
        * Its own section rather than a row inside the Informations card: it carries a refresh
        * action and a sync timestamp that nothing else in that card has, and the distance from the
        * hand-set badPayer checkbox is deliberate -- side by side they would read as one indicator.
        */}
      <CustomerReceivables customerId={data.id} variant="section" />

      {/*
        * The three notes in a section of their own, mirroring the intervention detail
        * (NotesSection) and the edit drawer: general, then technician, then planner. They used to
        * close the Informations card, where they read as three more settings among the report
        * template and the languages.
        *
        * Always rendered, with a "-" fallback like reportTemplate above, rather than hidden when
        * empty: on the profile these are settings, and a planner needs to see that a note slot
        * exists and is currently empty. The intervention screens are the opposite -- there an
        * empty note renders nothing at all.
        *
        * whitespace-pre-line because planners use newlines to write checklists; break-words because
        * nothing guarantees a space in 1000 characters of free text.
        */}
      <Typography variant="h4" className="sub-title">
        {t("client:notes:title")}
      </Typography>

      <Card className="details-card">
        <div className="field col-span-2">
          <label>{t("client:notes:general")}</label>
          <Typography className="whitespace-pre-line break-words">{data.notes || "-"}</Typography>
        </div>

        <div className="field col-span-2">
          <label>{t("client:form:fields:technicianNote:label")}</label>
          <Typography className="whitespace-pre-line break-words">{data.technicianNote || "-"}</Typography>
        </div>

        <div className="field col-span-2">
          <label>{t("client:form:fields:plannerNote:label")}</label>
          <Typography className="whitespace-pre-line break-words">{data.plannerNote || "-"}</Typography>
        </div>
      </Card>

      <Typography variant="h4" className="sub-title">
        {t("common:address")}
      </Typography>

      <Card className="details-card">
        {data.address && (
          <>
            <div className="field">
              <label>{t("common:address")}</label>
              <Typography>{data.address.address}</Typography>
            </div>

            <div className="field">
              <label>{t("common:complement")}</label>
              <Typography>{data.address.addressCompl}</Typography>
            </div>

            <div className="field">
              <label>{t("client:city")}</label>
              <Typography>{data.address.city}</Typography>
            </div>

            <div className="field">
              <label>{t("client:country")}</label>
              <Typography>{data.address.country}</Typography>
            </div>

            <div className="field">
              <label>{t("client:gps")}</label>
              <Typography>lat. {data.address.latitude}</Typography>
              <Typography>long. {data.address.longitude}</Typography>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default InformationsTab;
